import { useEffect, useState } from 'react';

export type AdminSection = 'scenarios' | 'audit' | 'content' | 'research' | 'agents' | 'graph' | 'guides';
export type ProgressTab = 'stages' | 'skills' | 'readiness' | 'tracks';
export type CoachTab = 'next' | 'ask' | 'profile';
/** Career page tabs: the overview (default) and the curriculum ladder. */
export type CareerTab = 'overview' | 'curriculum';

export type Route =
  | { page: 'home' }
  | { page: 'dashboard' }
  | { page: 'onboarding'; step?: string }
  | { page: 'career-map'; focus?: string }
  | { page: 'notifications' }
  | { page: 'assignments' }
  | { page: 'mentor' }
  | { page: 'coach'; tab?: CoachTab; q?: string }
  | { page: 'explore' }
  | { page: 'career'; id: string; tab?: CareerTab }
  | { page: 'compare'; a?: string; b?: string }
  | { page: 'workplace'; tab?: string; id?: string }
  | { page: 'scenario'; id: string; career?: string; session?: string }
  | { page: 'learn' }
  | { page: 'learn-path'; id: string }
  | { page: 'learn-lesson'; id: string }
  | { page: 'learn-lab'; id: string }
  | { page: 'learn-lab-session'; id: string }
  | { page: 'interview'; career?: string }
  | { page: 'interview-kit'; careerId: string }
  | { page: 'interview-session'; id: string; q?: string }
  | { page: 'certifications' }
  | { page: 'certification'; id: string }
  | { page: 'progress'; tab?: ProgressTab }
  | { page: 'projects'; career?: string }
  | { page: 'project'; id: string }
  | { page: 'project-session'; id: string }
  | { page: 'resume' }
  | { page: 'prep' }
  | { page: 'prep-bank'; id: string }
  | { page: 'prep-session'; id: string }
  | { page: 'prep-flashcards'; id: string }
  | { page: 'agent'; career?: string }
  | { page: 'research'; career?: string }
  | { page: 'reviewers' }
  | { page: 'review'; grantId?: string; view?: string }
  | { page: 'deliverables'; id?: string }
  | { page: 'program' }
  | { page: 'profile' }
  | { page: 'login' }
  | { page: 'register' }
  | { page: 'forgot' }
  | { page: 'reset'; token?: string }
  | { page: 'verify'; token?: string }
  | { page: 'admin'; section?: AdminSection; id?: string; type?: string }
  | { page: 'skills' }
  | { page: 'skill'; id: string }
  | { page: 'skill-map'; career?: string }
  | { page: 'foundations' }
  | { page: 'analytics' }
  | { page: 'knowledge' }
  | { page: 'knowledge-topic'; id: string }
  | { page: 'cheatsheets'; q?: string; category?: string }
  | { page: 'cheatsheet'; id: string }
  | { page: 'guides'; q?: string; domain?: string }
  | { page: 'guide'; id: string; level?: string; section?: string }
  | { page: 'resources'; skill?: string; career?: string; kind?: string; q?: string }
  | { page: 'assessments' }
  | { page: 'assessment'; id: string }
  | { page: 'assessment-session'; id: string }
  | { page: 'lab-workspace' }
  | { page: 'codelabs'; career?: string; language?: string }
  | { page: 'codelab'; id: string }
  | { page: 'codelab-session'; id: string }
  | { page: 'vmlabs'; career?: string }
  | { page: 'vmlab'; id: string }
  | { page: 'vm-session'; id: string }
  | { page: 'about' };

const dec = (s: string | undefined) => (s === undefined ? undefined : decodeURIComponent(s));

export function parseHash(hash: string): Route {
  const [path, query] = hash.replace(/^#\/?/, '').split('?');
  const params = new URLSearchParams(query ?? '');
  const parts = (path ?? '').split('/').filter(Boolean);
  switch (parts[0]) {
    case undefined:
      return { page: 'home' };
    case 'dashboard':
      return { page: 'dashboard' };
    case 'onboarding':
      return { page: 'onboarding', step: dec(parts[1]) };
    case 'careers':
      if (parts[1] === 'map') return { page: 'career-map', focus: params.get('focus') ?? undefined };
      return { page: 'explore' };
    case 'explore':
      return { page: 'explore' };
    case 'notifications':
      return { page: 'notifications' };
    case 'assignments':
      return { page: 'assignments' };
    case 'mentor':
      return { page: 'mentor' };
    case 'coach': {
      const tab = parts[1] === 'ask' || parts[1] === 'profile' ? parts[1] : undefined;
      const q = params.get('q') ?? undefined;
      return { page: 'coach', ...(tab ? { tab } : {}), ...(q ? { q } : {}) };
    }
    case 'portfolio':
      return { page: 'deliverables', id: dec(parts[1]) };
    case 'career':
      return parts[1] ? { page: 'career', id: decodeURIComponent(parts[1]), ...(params.get('tab') === 'curriculum' ? { tab: 'curriculum' as const } : {}) } : { page: 'explore' };
    case 'compare':
      return { page: 'compare', a: params.get('a') ?? undefined, b: params.get('b') ?? undefined };
    case 'workplace':
      return { page: 'workplace', tab: dec(parts[1]), id: dec(parts[2]) };
    case 'scenario':
      return parts[1] ? { page: 'scenario', id: decodeURIComponent(parts[1]), career: params.get('career') ?? undefined, session: params.get('session') ?? undefined } : { page: 'explore' };
    case 'learn': {
      const id = dec(parts[2]);
      switch (parts[1]) {
        case undefined:
          return { page: 'learn' };
        case 'path':
          return id ? { page: 'learn-path', id } : { page: 'learn' };
        case 'lesson':
          return id ? { page: 'learn-lesson', id } : { page: 'learn' };
        case 'lab':
          return id ? { page: 'learn-lab', id } : { page: 'learn' };
        case 'lab-session':
          return id ? { page: 'learn-lab-session', id } : { page: 'learn' };
        case 'certifications':
          return { page: 'certifications' };
        case 'certification':
          return id ? { page: 'certification', id } : { page: 'certifications' };
        default:
          return { page: 'learn' };
      }
    }
    case 'interview': {
      // `#/interview/session/:id` is the only two-part form; anything else names a career track.
      if (parts[1] === 'session') return parts[2] ? { page: 'interview-session', id: decodeURIComponent(parts[2]), q: params.get('q') ?? undefined } : { page: 'interview' };
      return parts[1] ? { page: 'interview-kit', careerId: decodeURIComponent(parts[1]) } : { page: 'interview', career: params.get('career') ?? undefined };
    }
    case 'progress': {
      const tab = parts[1];
      return tab === 'stages' || tab === 'skills' || tab === 'readiness' || tab === 'tracks' ? { page: 'progress', tab } : { page: 'progress' };
    }
    case 'projects':
      if (parts[1] === 'session') return parts[2] ? { page: 'project-session', id: decodeURIComponent(parts[2]) } : { page: 'projects' };
      return parts[1] ? { page: 'project', id: decodeURIComponent(parts[1]) } : { page: 'projects', career: params.get('career') ?? undefined };
    case 'resume':
      return { page: 'resume' };
    case 'prep': {
      const id = dec(parts[2]);
      if (parts[1] === 'session') return id ? { page: 'prep-session', id } : { page: 'prep' };
      if (parts[1] === 'flashcards') return id ? { page: 'prep-flashcards', id } : { page: 'prep' };
      return parts[1] ? { page: 'prep-bank', id: decodeURIComponent(parts[1]) } : { page: 'prep' };
    }
    case 'agent':
      return { page: 'agent', career: params.get('career') ?? undefined };
    case 'research':
      return { page: 'research', career: params.get('career') ?? undefined };
    case 'reviewers':
      return { page: 'reviewers' };
    case 'review':
      return { page: 'review', grantId: dec(parts[1]), view: dec(parts[2]) };
    case 'deliverables':
      return { page: 'deliverables', id: dec(parts[1]) };
    case 'program':
      return { page: 'program' };
    case 'profile':
      return { page: 'profile' };
    case 'login':
      return { page: 'login' };
    case 'register':
      return { page: 'register' };
    case 'forgot':
      return { page: 'forgot' };
    case 'reset':
      return { page: 'reset', token: params.get('token') ?? undefined };
    case 'verify':
      return { page: 'verify', token: params.get('token') ?? undefined };
    case 'admin':
      if (parts[1] === 'audit') return { page: 'admin', section: 'audit' };
      if (parts[1] === 'scenarios') return { page: 'admin', section: 'scenarios', id: dec(parts[2]) };
      if (parts[1] === 'content') return { page: 'admin', section: 'content', type: dec(parts[2]), id: dec(parts[3]) };
      if (parts[1] === 'research') return { page: 'admin', section: 'research', id: dec(parts[2]) };
      if (parts[1] === 'agents') return { page: 'admin', section: 'agents', id: dec(parts[2]) };
      if (parts[1] === 'graph') return { page: 'admin', section: 'graph' };
      if (parts[1] === 'guides') return { page: 'admin', section: 'guides', id: dec(parts[2]) };
      return { page: 'admin' };
    case 'skills':
      if (parts[1] === 'map') return { page: 'skill-map', career: params.get('career') ?? undefined };
      return { page: 'skills' };
    case 'skill':
      return parts[1] ? { page: 'skill', id: decodeURIComponent(parts[1]) } : { page: 'skills' };
    case 'foundations':
      return { page: 'foundations' };
    case 'analytics':
      return { page: 'analytics' };
    case 'knowledge':
      return parts[1] === 'topic' && parts[2] ? { page: 'knowledge-topic', id: decodeURIComponent(parts[2]) } : { page: 'knowledge' };
    case 'cheatsheets':
      return parts[1] ? { page: 'cheatsheet', id: decodeURIComponent(parts[1]) } : { page: 'cheatsheets', q: params.get('q') ?? undefined, category: params.get('category') ?? undefined };
    case 'guides': {
      if (parts[1]) {
        const level = dec(parts[2]);
        const section = params.get('section') ?? undefined;
        return { page: 'guide', id: decodeURIComponent(parts[1]), ...(level ? { level } : {}), ...(section ? { section } : {}) };
      }
      const q = params.get('q') ?? undefined;
      const domain = params.get('domain') ?? undefined;
      return { page: 'guides', ...(q ? { q } : {}), ...(domain ? { domain } : {}) };
    }
    case 'resources':
      return { page: 'resources', skill: params.get('skill') ?? undefined, career: params.get('career') ?? undefined, kind: params.get('kind') ?? undefined, q: params.get('q') ?? undefined };
    case 'assessments':
      if (parts[1] === 'session') return parts[2] ? { page: 'assessment-session', id: decodeURIComponent(parts[2]) } : { page: 'assessments' };
      return parts[1] ? { page: 'assessment', id: decodeURIComponent(parts[1]) } : { page: 'assessments' };
    case 'lab-workspace':
      return { page: 'lab-workspace' };
    case 'codelabs':
      if (parts[1] === 'session') return parts[2] ? { page: 'codelab-session', id: decodeURIComponent(parts[2]) } : { page: 'codelabs' };
      return parts[1] ? { page: 'codelab', id: decodeURIComponent(parts[1]) } : { page: 'codelabs', career: params.get('career') ?? undefined, language: params.get('language') ?? undefined };
    case 'vmlabs':
      if (parts[1] === 'session') return parts[2] ? { page: 'vm-session', id: decodeURIComponent(parts[2]) } : { page: 'vmlabs' };
      return parts[1] ? { page: 'vmlab', id: decodeURIComponent(parts[1]) } : { page: 'vmlabs', career: params.get('career') ?? undefined };
    case 'about':
      return { page: 'about' };
    default:
      return { page: 'home' };
  }
}

export function href(route: Route): string {
  const enc = encodeURIComponent;
  switch (route.page) {
    case 'home':
      return '#/';
    case 'dashboard':
      return '#/dashboard';
    case 'onboarding':
      return `#/onboarding${route.step ? `/${enc(route.step)}` : ''}`;
    case 'career-map':
      return `#/careers/map${route.focus ? `?focus=${enc(route.focus)}` : ''}`;
    case 'notifications':
      return '#/notifications';
    case 'assignments':
      return '#/assignments';
    case 'mentor':
      return '#/mentor';
    case 'coach':
      return `#/coach${route.tab && route.tab !== 'next' ? `/${route.tab}` : ''}${route.q ? `?q=${enc(route.q)}` : ''}`;
    case 'explore':
      return '#/careers';
    case 'career':
      return `#/career/${enc(route.id)}${route.tab === 'curriculum' ? '?tab=curriculum' : ''}`;
    case 'compare': {
      const q = new URLSearchParams();
      if (route.a) q.set('a', route.a);
      if (route.b) q.set('b', route.b);
      const s = q.toString();
      return `#/compare${s ? `?${s}` : ''}`;
    }
    case 'workplace':
      return `#/workplace${route.tab ? `/${enc(route.tab)}` : ''}${route.tab && route.id ? `/${enc(route.id)}` : ''}`;
    case 'scenario': {
      const q = new URLSearchParams();
      if (route.career) q.set('career', route.career);
      if (route.session) q.set('session', route.session);
      const s = q.toString();
      return `#/scenario/${enc(route.id)}${s ? `?${s}` : ''}`;
    }
    case 'learn':
      return '#/learn';
    case 'learn-path':
      return `#/learn/path/${enc(route.id)}`;
    case 'learn-lesson':
      return `#/learn/lesson/${enc(route.id)}`;
    case 'learn-lab':
      return `#/learn/lab/${enc(route.id)}`;
    case 'learn-lab-session':
      return `#/learn/lab-session/${enc(route.id)}`;
    case 'certifications':
      return '#/learn/certifications';
    case 'certification':
      return `#/learn/certification/${enc(route.id)}`;
    case 'interview':
      return `#/interview${route.career ? `?career=${enc(route.career)}` : ''}`;
    case 'interview-kit':
      return `#/interview/${enc(route.careerId)}`;
    case 'interview-session':
      return `#/interview/session/${enc(route.id)}${route.q ? `?q=${enc(route.q)}` : ''}`;
    case 'progress':
      return `#/progress${route.tab ? `/${route.tab}` : ''}`;
    case 'projects':
      return `#/projects${route.career ? `?career=${enc(route.career)}` : ''}`;
    case 'project':
      return `#/projects/${enc(route.id)}`;
    case 'project-session':
      return `#/projects/session/${enc(route.id)}`;
    case 'resume':
      return '#/resume';
    case 'prep':
      return '#/prep';
    case 'prep-bank':
      return `#/prep/${enc(route.id)}`;
    case 'prep-session':
      return `#/prep/session/${enc(route.id)}`;
    case 'prep-flashcards':
      return `#/prep/flashcards/${enc(route.id)}`;
    case 'agent':
      return `#/agent${route.career ? `?career=${enc(route.career)}` : ''}`;
    case 'research':
      return `#/research${route.career ? `?career=${enc(route.career)}` : ''}`;
    case 'reviewers':
      return '#/reviewers';
    case 'review':
      return `#/review${route.grantId ? `/${enc(route.grantId)}` : ''}${route.grantId && route.view ? `/${enc(route.view)}` : ''}`;
    case 'deliverables':
      return `#/portfolio${route.id ? `/${enc(route.id)}` : ''}`;
    case 'program':
      return '#/program';
    case 'profile':
      return '#/profile';
    case 'login':
      return '#/login';
    case 'register':
      return '#/register';
    case 'forgot':
      return '#/forgot';
    case 'reset':
      return `#/reset${route.token ? `?token=${enc(route.token)}` : ''}`;
    case 'verify':
      return `#/verify${route.token ? `?token=${enc(route.token)}` : ''}`;
    case 'admin':
      if (route.section === 'audit') return '#/admin/audit';
      if (route.section === 'scenarios') return `#/admin/scenarios${route.id ? `/${enc(route.id)}` : ''}`;
      if (route.section === 'content') return `#/admin/content${route.type ? `/${enc(route.type)}` : ''}${route.type && route.id ? `/${enc(route.id)}` : ''}`;
      if (route.section === 'research') return `#/admin/research${route.id ? `/${enc(route.id)}` : ''}`;
      if (route.section === 'agents') return `#/admin/agents${route.id ? `/${enc(route.id)}` : ''}`;
      if (route.section === 'graph') return '#/admin/graph';
      if (route.section === 'guides') return `#/admin/guides${route.id ? `/${enc(route.id)}` : ''}`;
      return '#/admin';
    case 'skills':
      return '#/skills';
    case 'skill':
      return `#/skill/${enc(route.id)}`;
    case 'skill-map':
      return `#/skills/map${route.career ? `?career=${enc(route.career)}` : ''}`;
    case 'foundations':
      return '#/foundations';
    case 'analytics':
      return '#/analytics';
    case 'knowledge':
      return '#/knowledge';
    case 'knowledge-topic':
      return `#/knowledge/topic/${enc(route.id)}`;
    case 'cheatsheets': {
      const q = new URLSearchParams();
      if (route.q) q.set('q', route.q);
      if (route.category) q.set('category', route.category);
      const s = q.toString();
      return `#/cheatsheets${s ? `?${s}` : ''}`;
    }
    case 'cheatsheet':
      return `#/cheatsheets/${enc(route.id)}`;
    case 'guides': {
      const q = new URLSearchParams();
      if (route.q) q.set('q', route.q);
      if (route.domain) q.set('domain', route.domain);
      const s = q.toString();
      return `#/guides${s ? `?${s}` : ''}`;
    }
    case 'guide':
      return `#/guides/${enc(route.id)}${route.level ? `/${enc(route.level)}` : ''}${route.section ? `?section=${enc(route.section)}` : ''}`;
    case 'resources': {
      const q = new URLSearchParams();
      if (route.skill) q.set('skill', route.skill);
      if (route.career) q.set('career', route.career);
      if (route.kind) q.set('kind', route.kind);
      if (route.q) q.set('q', route.q);
      const s = q.toString();
      return `#/resources${s ? `?${s}` : ''}`;
    }
    case 'assessments':
      return '#/assessments';
    case 'assessment':
      return `#/assessments/${enc(route.id)}`;
    case 'assessment-session':
      return `#/assessments/session/${enc(route.id)}`;
    case 'lab-workspace':
      return '#/lab-workspace';
    case 'codelabs': {
      const q = new URLSearchParams();
      if (route.career) q.set('career', route.career);
      if (route.language) q.set('language', route.language);
      const s = q.toString();
      return `#/codelabs${s ? `?${s}` : ''}`;
    }
    case 'codelab':
      return `#/codelabs/${enc(route.id)}`;
    case 'codelab-session':
      return `#/codelabs/session/${enc(route.id)}`;
    case 'vmlabs':
      return `#/vmlabs${route.career ? `?career=${enc(route.career)}` : ''}`;
    case 'vmlab':
      return `#/vmlabs/${enc(route.id)}`;
    case 'vm-session':
      return `#/vmlabs/session/${enc(route.id)}`;
    case 'about':
      return '#/about';
  }
}

export function navigate(route: Route): void {
  window.location.hash = href(route);
}

export function useRoute(): Route {
  const [route, setRoute] = useState<Route>(() => parseHash(window.location.hash));
  useEffect(() => {
    const onChange = () => {
      setRoute(parseHash(window.location.hash));
      // Move focus to the main landmark so keyboard and screen-reader users land on the new page.
      const main = document.getElementById('main');
      main?.focus();
    };
    window.addEventListener('hashchange', onChange);
    return () => window.removeEventListener('hashchange', onChange);
  }, []);
  return route;
}
